import React, { useState, ChangeEvent } from 'react';
import { utils } from 'kb-shared';
import { capitalize } from 'lodash';
import { ApolloError } from 'apollo-client';
import { DropdownOption } from 'kb-shared';
import Honeybadger from '@honeybadger-io/js';

import { FieldsRow, Icon, Text, InputsLabel } from '../shared';
import { validateAddressFields } from '../helpers';
import { paperplane, phone as phoneIcon, mailing } from '../../../../assets/icons';
import { showErrorToast } from '../../../../utilities/notificationUtils';
import TextInput from '../../../../components/TextInput/NewTextInput';
import SelectInput from '../../../../components/SelectInput';

import {
  MailingWrapper,
  AddressRow,
  CityWrapper,
  CountryWrapper,
  Field,
  EmailDetail,
  EmailValue,
  InputRow
} from './ContactInfoFields.styles';
import { useContactInfoFieldsMutations } from './ContactInfoFields.graphql';

const { states, COUNTRIES_OPTIONS, PREFERRED_TIMEZONE_OPTIONS } = utils;

type Props = {
  address1?: string;
  address2?: string;
  city?: string;
  state?: string;
  zipcode?: string;
  email?: string;
  phone?: string;
  country?: string;
  preferredTimezone?: string;
};

type Address = {
  address1?: string;
  address2?: string;
  city?: string;
  state?: string;
  zipcode?: string;
  country: string;
};

const COUNTRY_UNITED_STATE = ['United States', 'USA'];

export default function ContactFields({
  address1,
  address2,
  city,
  state,
  zipcode,
  email,
  phone,
  country = '',
  preferredTimezone
}: Props) {
  const isEmptyCountry = country === undefined;
  const isUS = (country: string) => COUNTRY_UNITED_STATE.includes(country);

  const initialStateOption = states.find(item => item.value === state);
  const initialCountryOption = COUNTRIES_OPTIONS.find(item => item.value === country);
  const initialPreferredTimezoneOption = PREFERRED_TIMEZONE_OPTIONS.find(
    item => item.value === preferredTimezone
  );

  const { updatePatient, updateAddress } = useContactInfoFieldsMutations();
  const [address, updateAddressDetails] = useState<Address>({
    address1,
    address2,
    city,
    state,
    zipcode,
    country
  });
  const [phoneDetails, updatePhoneDetails] = useState(phone);
  const [countrySelectedOption, updateCountrySelectedOption] = useState(initialCountryOption);
  const [preferredTimezoneSelectedOption, updatePreferredTimezoneSelectedOption] = useState(
    initialPreferredTimezoneOption
  );

  const handleAddressChange = (e: ChangeEvent<HTMLInputElement>) => {
    updateAddressDetails({
      ...address,
      [e.target.name]: e.target.value
    });
  };
  const handleStateChange = (option: DropdownOption) => {
    updateAddressDetails({ ...address, state: option.value });
    const errors = validateAddressFields(address);
    if (errors.length > 0) {
      showErrorToast(`${errors.join(', ')} can't be blank`);
    } else {
      updateAddress({
        variables: {
          ...address,
          state: option.value
        }
      });
    }
  };
  const handleAddressBlur = () => {
    const errors = isUS(address.country) ? validateAddressFields(address) : [];

    if (errors.length > 0) {
      const connector = errors.length > 2 ? ',' : '';
      const errorMessage =
        errors.length > 1
          ? `${errors.slice(0, -1).join(', ')}${connector} and ${
              errors[errors.length - 1]
            } can't be blank`
          : `${errors.join(', ')} can't be blank`;

      showErrorToast(capitalize(errorMessage));
    } else {
      updateAddress({
        variables: {
          ...address
        }
      }).catch((res: ApolloError) => {
        const errors = res.graphQLErrors.map(error => {
          return JSON.parse(error.message);
        });
        const errorCopy = errors[0].join(', ');
        return showErrorToast(errorCopy);
      });
    }
  };
  const handleCountryChange = (option: DropdownOption) => {
    const updatedAdress = {
      ...address,
      country: option.value,
      state: isUS(option.value) ? initialStateOption?.value : '',
      zipcode: ''
    };

    updateAddressDetails(updatedAdress);
    updateAddress({ variables: updatedAdress });
    updateCountrySelectedOption(option);
  };
  const handlePreferredTimezone = (option: DropdownOption) => {
    try {
      updatePreferredTimezoneSelectedOption({ label: option.value, value: option.value });
      updatePatient({
        variables: {
          preferredTimezone: option.value
        }
      });
    } catch (e) {
      Honeybadger.notify(e);
      const apolloError = e as ApolloError;
      const [error] = apolloError.graphQLErrors.map(error => JSON.parse(error.message));
      const errorCopy = error.join(', ');
      return showErrorToast(errorCopy);
    }
  };

  return (
    <>
      <InputRow>
        <Field>
          <InputsLabel>
            <Icon src={paperplane} />
            <Text>Email Address</Text>
          </InputsLabel>
          <EmailDetail>VERIFIED EMAIL CANNOT BE EDITED</EmailDetail>
          <EmailValue>{email}</EmailValue>
        </Field>
        <Field>
          <InputsLabel>
            <Icon src={phoneIcon} />
            <Text>Telephone number</Text>
          </InputsLabel>
          <TextInput
            inputType={isUS(address.country) ? 'phone' : 'text'}
            placeholder="(+XXXX)Phone Number"
            name="phone"
            autoComplete="tel"
            label="mobile or phone number"
            value={phoneDetails}
            onChange={(e: ChangeEvent<HTMLInputElement>) => updatePhoneDetails(e.target.value)}
            onBlur={() => {
              updatePatient({
                variables: {
                  phone: phoneDetails
                }
              }).catch((res: ApolloError) => {
                const errors = res.graphQLErrors.map(error => {
                  return JSON.parse(error.message);
                });
                const errorCopy = errors[0].join(', ');
                return showErrorToast(errorCopy);
              });
            }}
          />
        </Field>
      </InputRow>
      <FieldsRow>
        <InputsLabel>
          <Icon src={mailing} />
          <Text>Mailing address</Text>
        </InputsLabel>
        <CountryWrapper>
          <SelectInput
            label="Country"
            extractTitle={item => {
              return item ? item.label : '';
            }}
            value={address.country}
            selectedOption={countrySelectedOption}
            onSelect={handleCountryChange}
            placeholder={'Select country'}
            options={COUNTRIES_OPTIONS}
          />
        </CountryWrapper>
        <InputRow>
          <TextInput
            name="address1"
            className="address1-field"
            label="address line 1"
            value={address.address1}
            onChange={handleAddressChange}
            onBlur={handleAddressBlur}
          />
          <TextInput
            name="address2"
            label="address line 2"
            value={address.address2}
            onChange={handleAddressChange}
            onBlur={handleAddressBlur}
          />
        </InputRow>
      </FieldsRow>
      <AddressRow>
        <CityWrapper>
          <TextInput
            name="city"
            label="city"
            value={address.city}
            onBlur={handleAddressBlur}
            onChange={handleAddressChange}
          />
        </CityWrapper>

        {isEmptyCountry || isUS(address.country) ? (
          <MailingWrapper>
            <SelectInput
              label="state"
              type="select"
              placeholder="Select state"
              selectedOption={initialStateOption}
              extractTitle={item => (item ? item.label : '')}
              options={states}
              onSelect={handleStateChange}
            />
            <TextInput
              name="zipcode"
              label="zip code"
              inputType="zip"
              value={address.zipcode}
              onChange={handleAddressChange}
              autoComplete="postal-code"
              onBlur={handleAddressBlur}
            />
          </MailingWrapper>
        ) : (
          <MailingWrapper>
            <TextInput
              name="state"
              label="State"
              placeholder="State"
              value={address.state}
              onBlur={handleAddressBlur}
              onChange={handleAddressChange}
            />
            <TextInput
              name="zipcode"
              label="zip code"
              placeholder="ZIP"
              value={address.zipcode}
              onBlur={handleAddressBlur}
              onChange={handleAddressChange}
            />
          </MailingWrapper>
        )}
      </AddressRow>
      <AddressRow>
        <SelectInput
          label="Preferred Timezone"
          extractTitle={item => (item ? item.label : '')}
          value={preferredTimezone}
          selectedOption={preferredTimezoneSelectedOption}
          onSelect={handlePreferredTimezone}
          placeholder="Select preferred timezone"
          options={PREFERRED_TIMEZONE_OPTIONS}
        />
      </AddressRow>
    </>
  );
}
